import { useTranslation } from 'react-i18next';
import { useTemplateSelection } from '../../hooks/useTemplateSelection';
import type { GeneratedTemplateType } from '../../types/util-types';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import HBDropdown from '../honeybook-ui/HBDropdown/HBDropdown';
import './template-generator-form.scss';
import { useState, useEffect, useMemo } from 'react';
import type { OptionTypeBase } from 'react-select';
import HBMediaElement from '../honeybook-ui/HBMediaElement/HBMediaElement';
import AnalyticsService from '../../Services/AnalyticsService';
import { AnalyticsEvents } from '../../Services/AnalyticsService';
import UtilsService from '../../Services/UtilsService';

interface Props {
  templateType: GeneratedTemplateType;
}

// Specific utility functions for each label type
const getIndustryLabel = (label: string | null | undefined, t: (key: string) => string): string => 
  UtilsService.calculateVowelLabel(label, 'TemplateGeneratorForm.industry', t);

const getTemplateStyleLabel = (label: string | null | undefined, t: (key: string) => string): string => 
  UtilsService.calculateVowelLabel(label, 'TemplateGeneratorForm.templateStyle', t);

const TemplateGeneratorForm = ({ templateType }: Props): JSX.Element => {
  const { t } = useTranslation();
  const {
    companyType,
    selectedTemplate,
    companyTypeOptions,
    templateOptions,
    storeTemplateSelection,
    setCompanyType,
    setSelectedTemplate,
    prefilledCompanyType
  } = useTemplateSelection(templateType);

  const [userSelectedCompany, setUserSelectedCompany] = useState(prefilledCompanyType);
  const [selectedTemplateOption, setSelectedTemplateOption] = useState<OptionTypeBase | null>(null);
  const [industryLabel, setIndustryLabel] = useState<string>(t('TemplateGeneratorForm.industry'));
  const [templateStyleLabel, setTemplateStyleLabel] = useState<string>(t('TemplateGeneratorForm.templateStyle'));

  // Create a CSS class name for the company dropdown based on whether we have a prefilled value
  const companyDropdownClass = useMemo(() => {
    const baseClass = "template-generator-form__company-dropdown";
    return (!prefilledCompanyType && !userSelectedCompany) 
      ? `${baseClass} template-generator-form__company-dropdown--animate` 
      : baseClass;
  }, [prefilledCompanyType, userSelectedCompany]);

  useEffect(() => {
    if ((userSelectedCompany || prefilledCompanyType) && selectedTemplate && templateOptions.length > 0) {
      const matchingOption = templateOptions.find(opt => opt.value === selectedTemplate);
      if (matchingOption) {
        setSelectedTemplateOption(matchingOption);
        setTemplateStyleLabel(getTemplateStyleLabel(matchingOption.label, t));
      }
    }
  }, [selectedTemplate, templateOptions, userSelectedCompany, prefilledCompanyType, t]);

  useEffect(() => {
    if (prefilledCompanyType && companyType && companyTypeOptions.length > 0) {
      const matchingCompanyOption = companyTypeOptions.find(opt => opt.value === companyType);
      if (matchingCompanyOption) {
        setUserSelectedCompany(true);
        setIndustryLabel(getIndustryLabel(matchingCompanyOption.label, t));
      }
    }
  }, [prefilledCompanyType, companyType, companyTypeOptions, t]);

  const handleCompanyTypeChange = (selected: OptionTypeBase): void => {
    if (selected?.value) {
      setCompanyType(selected.value);
      setUserSelectedCompany(true);
      setIndustryLabel(getIndustryLabel(selected.label, t));
    }
  };

  const handleTemplateChange = (selected: OptionTypeBase): void => {
    if (selected?.value) {
      setSelectedTemplate(selected.value);
      setTemplateStyleLabel(getTemplateStyleLabel(selected.label, t));
    }
  };

  if (companyType && templateOptions.length === 0) {
    return (
      <div className="template-generator-form">
        <p>No templates available for this type.</p>
      </div>
    );
  }

  const handleBeforeSubmitCallback = async (): Promise<void> => {
    storeTemplateSelection();
    AnalyticsService.trackClick(AnalyticsEvents.generate_contract, {
      generatedTemplateType: templateType,
      companyType: companyType,
      templateType: selectedTemplate
    });
    await new Promise(resolve => setTimeout(resolve, 2000));
  };

  const companyDropdownDefaultValue = prefilledCompanyType ? 
    companyTypeOptions.find(opt => opt.value === companyType) : 
    undefined;

  return (
    <div className="template-generator-form">
      <div className='template-generator-form__form-wrapper'>
        <HBMediaElement
          name='marketing_site/ai_templates/try_me.png'
          alt='try me'
          lazyLoad={false}
          customClass='template-generator-form__media'
        />
        <HBDropdown
          label={industryLabel}
          options={companyTypeOptions}
          onChange={handleCompanyTypeChange}
          isValid
          customClass={companyDropdownClass}
          defaultValue={companyDropdownDefaultValue}
        />

        <HBDropdown
          key={`template-dropdown-${selectedTemplateOption?.value || 'placeholder'}`}
          label={templateStyleLabel}
          options={templateOptions}
          onChange={handleTemplateChange}
          placeholder={`type of ${templateType}`}
          isValid
          customClass="template-generator-form__dropdown template-generator-form__template-dropdown"
          defaultValue={selectedTemplateOption}
          isSearchable={false}
        />
      </div>

      <div className="template-generator-form__cta-wrapper">
        <RegistrationForm
          source="template_generator"
          buttonText={t('TemplateGeneratorForm.ctaButton') as string}
          onBeforeSubmit={handleBeforeSubmitCallback}
        />
      </div>
    </div>
  );
};

export default TemplateGeneratorForm;
